import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { RouteSelectionController } from "../../../hooks/useRouteSelection";
import type { VillageReferenceRecord, VillageViewModel } from "../../../domain/contracts";
import {
  villageEvidenceQueryOptions,
  villageIntervieweesQueryOptions,
  villageSummaryQueryOptions,
} from "../../../services/queries/publicQueryOptions";
import {
  buildEvidenceEntryLabel,
  buildEvidenceEntryType,
  buildEvidenceOverviewLines,
} from "../../../services/evidenceLabels";
import { formatDateLabel, formatNumber, toVillageViewModel } from "../../../services/viewModels";

type VillageControllerArgs = {
  routeSelection: RouteSelectionController;
  reference: VillageReferenceRecord[];
  selectedVillageRecord: VillageViewModel | null;
  selectedVillageBackendRef: string | null;
};

export function useVillageController({ routeSelection, reference, selectedVillageRecord, selectedVillageBackendRef }: VillageControllerArgs) {
  const [isIncidentExpanded, setIncidentExpanded] = useState(false);
  const villageSummaryQuery = useQuery(villageSummaryQueryOptions(selectedVillageBackendRef));

  const selectedVillage = useMemo(() => {
    const summaryVillage = villageSummaryQuery.data?.village;
    if (!summaryVillage) {
      return selectedVillageRecord;
    }

    return toVillageViewModel(summaryVillage, reference);
  }, [reference, selectedVillageRecord, villageSummaryQuery.data?.village]);

  const intervieweesQuery = useInfiniteQuery(villageIntervieweesQueryOptions(selectedVillageBackendRef));

  const evidenceQuery = useQuery(villageEvidenceQueryOptions(selectedVillageBackendRef, Boolean(selectedVillageBackendRef)));

  const interviewees = useMemo(
    () => intervieweesQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [intervieweesQuery.data?.pages],
  );

  const intervieweeDirectorySummary = interviewees.length > 0
    ? `${formatNumber(interviewees.length)} loaded of ${formatNumber(intervieweesQuery.data?.pages[0]?.total ?? interviewees.length)} protected-preview interviewees.`
    : intervieweesQuery.isLoading
      ? "Loading protected-preview interviewees..."
    : "No protected-preview interviewees have been published for this village yet.";

  const evidenceOverviewLines = useMemo(() => {
    const evidenceItems = evidenceQuery.data?.items ?? [];
    return buildEvidenceOverviewLines(evidenceItems, 3, formatNumber);
  }, [evidenceQuery.data?.items]);

  const incidentEntries = useMemo(() => {
    const sorted = [...interviewees].sort((left, right) => {
      const leftCount = Number.isFinite(left.incidentCount) ? left.incidentCount : 0;
      const rightCount = Number.isFinite(right.incidentCount) ? right.incidentCount : 0;
      if (rightCount !== leftCount) {
        return rightCount - leftCount;
      }
      return (right.updatedAt ?? "").localeCompare(left.updatedAt ?? "");
    });
    const mapped = sorted.map((interviewee) => ({
      id: interviewee.id,
      date: interviewee.updatedAt.slice(0, 10),
      interviewee: interviewee.publicPseudonym,
      incidentCount: interviewee.incidentCount,
    }));
    const limit = isIncidentExpanded ? mapped.length : 6;
    return mapped.slice(0, limit);
  }, [interviewees, isIncidentExpanded]);

  const incidentOverview = useMemo(() => {
    const total = interviewees.length;
    return {
      total,
      showToggle: total > 6,
      isExpanded: isIncidentExpanded,
      toggleLabel: isIncidentExpanded ? "Show less" : "Show more",
    };
  }, [interviewees.length, isIncidentExpanded]);

  const evidenceEntries = useMemo(() => {
    const categoryCounters = new Map<string, number>();

    return (evidenceQuery.data?.items ?? []).slice(0, 6).map((item) => {
      const nextIndex = (categoryCounters.get(item.category) ?? 0) + 1;
      categoryCounters.set(item.category, nextIndex);

      const label = buildEvidenceEntryLabel(item, nextIndex);

      return {
        id: item.id,
        label,
        type: buildEvidenceEntryType(item),
      };
    });
  }, [evidenceQuery.data?.items]);

  return {
    selectedVillage,
    interviewees,
    evidenceItems: evidenceQuery.data?.items ?? [],
    loading: {
      isIntervieweesLoading: intervieweesQuery.isLoading,
      isEvidenceLoading: evidenceQuery.isLoading,
    },
    summaryLines: selectedVillage?.backendRef
      ? [
          `${formatNumber(selectedVillage.stats.interviewCount)} testimonies`,
          `${formatNumber(selectedVillage.stats.evidenceItemCount)} evidence items`,
          `${formatNumber(selectedVillage.stats.siteMediaCount)} site media`,
        ]
      : [],
    villageFacts: selectedVillage?.reference
      ? {
          status2017: selectedVillage.reference.status2017,
          status2019: selectedVillage.reference.status2019,
          reconstruction: selectedVillage.reference.reconstruction,
          reconstructionType: selectedVillage.reference.reconstructionType,
          dateAdded: formatDateLabel(selectedVillage.reference.dateAdded),
          published: selectedVillage.reference.published,
        }
      : null,
    intervieweeDirectorySummary,
    evidenceOverviewLines,
    incidentEntries,
    incidentOverview,
    evidenceEntries,
    pagination: {
      canLoadMoreInterviewees: Boolean(intervieweesQuery.hasNextPage),
      isLoadingMoreInterviewees: intervieweesQuery.isFetchingNextPage,
    },
    handlers: {
      selectInterviewee(intervieweeRef: string) {
        routeSelection.setIntervieweeRef(intervieweeRef);
      },
      loadMoreInterviewees() {
        void intervieweesQuery.fetchNextPage();
      },
      toggleIncidentOverview() {
        setIncidentExpanded((prev) => !prev);
      },
    },
  };
}